import { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Popover } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-final-form';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'lodash';
import { format, parseISO } from 'date-fns';

import TextField from 'common/forms/TextField';
import DateField from 'common/forms/DateField';
import { casingFormat } from 'common/utils';
import { updateCentralFillQueueFilter } from 'store/thunks/workQueueThunks';
import { listCentralFillPharmaciesApi } from 'api/centralFill';

import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';

const ERROR_STATUSES = [
  'SUBMIT_FAILED',
  'CLINIC_NOT_CONFIGURED',
  'CLINIC_AUTOCREATE_FAILED',
  'PROVIDER_RESOLUTION_FAILED',
  'PATIENT_RESOLUTION_FAILED',
  'PRODUCT_NOT_MAPPED',
  'EXCEPTION',
  'MISSING_REASON_FOR_COMPOUNDING',
];

const STATUS_OPTIONS = [
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SUBMITTED', label: 'Submitted' },
  { value: 'ACCEPTED', label: 'Accepted' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'ROUTE_TO_DEFAULT', label: 'Route to Default' },
  { value: 'ERROR', label: 'Error' },
];

const CentralFillQueueFilterButton = () => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState(null);
  const [pharmacies, setPharmacies] = useState([]);
  const [formInitialValues, setFormInitialValues] = useState({});

  const filter = useSelector(({ workQueue }) => workQueue.compoundingQueue.filter);
  const loading = useSelector(({ workQueue }) => workQueue.compoundingQueue.loading);

  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const partnerOptions = useMemo(
    () =>
      partners?.map(({ partnerName, partnerUuid }) => ({
        value: partnerUuid,
        label: casingFormat(partnerName),
      })) ?? [],
    [partners]
  );

  const pharmacyOptions = useMemo(
    () =>
      pharmacies.map(({ compoundingPharmacyId, name }) => ({
        value: compoundingPharmacyId,
        label: name,
      })),
    [pharmacies]
  );

  useEffect(() => {
    listCentralFillPharmaciesApi({ isActive: true })
      .then((result) => {
        if (result) setPharmacies(result);
      })
      .catch(() => {});
  }, []);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    const selectedPartners = partnerOptions.filter((p) => filter?.partnerIds?.includes(p.value));
    const selectedPharmacies = pharmacyOptions.filter((p) =>
      filter?.compoundingPharmacyIds?.includes(p.value)
    );
    const hasErrorSelected = filter?.statuses?.some((s) => ERROR_STATUSES.includes(s));
    const selectedStatuses = [
      ...STATUS_OPTIONS.filter(
        (opt) => opt.value !== 'ERROR' && filter?.statuses?.includes(opt.value)
      ),
      ...(hasErrorSelected ? [STATUS_OPTIONS.find((o) => o.value === 'ERROR')] : []),
    ];

    setFormInitialValues({
      ...filter,
      partnerIds: selectedPartners,
      compoundingPharmacyIds: selectedPharmacies,
      statuses: selectedStatuses,
      submittedAfter: filter?.submittedAfter ? parseISO(filter.submittedAfter) : undefined,
      submittedBefore: filter?.submittedBefore ? parseISO(filter.submittedBefore) : undefined,
    });
  };

  const handleClose = () => setAnchorEl(null);

  const popoverId = anchorEl ? 'WorkQueue-CentralFillQueueFilterButton-Popover' : undefined;

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);
    dispatch(
      updateCentralFillQueueFilter({
        filter: {
          orderId: formData.orderId || undefined,
          drugNameContains: formData.drugNameContains || undefined,
          submittedAfter: formData.submittedAfter
            ? format(formData.submittedAfter, 'yyyy-MM-dd')
            : undefined,
          submittedBefore: formData.submittedBefore
            ? format(formData.submittedBefore, 'yyyy-MM-dd')
            : undefined,
          statuses: !isEmpty(formData.statuses)
            ? formData.statuses.flatMap((s) => (s.value === 'ERROR' ? ERROR_STATUSES : [s.value]))
            : undefined,
          partnerIds: !isEmpty(formData.partnerIds)
            ? formData.partnerIds.map((p) => p.value)
            : undefined,
          compoundingPharmacyIds: !isEmpty(formData.compoundingPharmacyIds)
            ? formData.compoundingPharmacyIds.map((p) => p.value)
            : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>

      <Popover
        id={popoverId}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '70vh',
              overflowY: 'auto',
            },
          },
        }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, values }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order ID'
                      name='orderId'
                      id='WorkQueue-CentralFillQueueFilterButton-orderId-input'
                    />
                  </Grid>

                  <MultiPracticeSelectField
                    label='Status'
                    name='statuses'
                    id='WorkQueue-CentralFillQueueFilterButton-statuses-dropdown'
                    options={STATUS_OPTIONS}
                  />

                  <MultiPracticeSelectField
                    label='Partner'
                    name='partnerIds'
                    id='WorkQueue-CentralFillQueueFilterButton-partner-dropdown'
                    options={partnerOptions}
                  />

                  <MultiPracticeSelectField
                    label='Compounding Pharmacy'
                    name='compoundingPharmacyIds'
                    id='WorkQueue-CentralFillQueueFilterButton-pharmacy-dropdown'
                    options={pharmacyOptions}
                  />

                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Drug Name'
                      name='drugNameContains'
                      id='WorkQueue-CentralFillQueueFilterButton-drugName-input'
                      maxLength={300}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Submitted From'
                      name='submittedAfter'
                      id='WorkQueue-CentralFillQueueFilterButton-submittedAfter-input'
                      maxDate={values.submittedBefore || undefined}
                    />
                  </Grid>

                  <Grid item sx={{ py: 0.5 }}>
                    <DateField
                      label='Submitted To'
                      name='submittedBefore'
                      id='WorkQueue-CentralFillQueueFilterButton-submittedBefore-input'
                      minDate={values.submittedAfter || undefined}
                    />
                  </Grid>

                  <Grid container justifyContent='flex-end'>
                    <LoadingButton
                      loading={loading}
                      type='submit'
                      variant='contained'
                      disabled={invalid}
                    >
                      Apply
                    </LoadingButton>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default CentralFillQueueFilterButton;
